import {
  Block,
  CreateForm,
  DialpadIcon,
  InternalLink,
  Spacing,
  commitWorkflowStepByName,
  moveWorkflowForwardByName,
  useScopedSelector,
} from 'ia-react-core';
import { FormPageTitle, FormSection, InformationsSection } from 'omni-package-react-core';
import React, { FC, ReactNode, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { MfaValidationPageState } from '../../../../interfaces/MfaValidationPageState';
import { getIdentityManagementApiRoot } from '../../../../utils/getIdentityManagementApiRoot';
import { MfaMethod } from '../../../../interfaces/MfaMethod';
import { MfaInformationType } from '../../../../interfaces/MfaInformationType';
import MfaActionBar from '../MfaActionBar';
import { MfaInformations } from '../../../../interfaces/MfaInformations';
import Constants from '../../../../Constants';
import { StyledSubmitButton } from './MfaCodeGeneration.styles';

export interface MfaCodeGenerationProps {
  method: MfaMethod;
  informationType: MfaInformationType;
  submitButtonLabel: string;
  formatInfo?: (info: string) => string;
  children: ReactNode;
}

const MfaCodeGeneration: FC<MfaCodeGenerationProps> = ({ method, informationType, children, submitButtonLabel, formatInfo = (info) => info }) => {
  const { t } = useTranslation('MfaValidationPage');
  const dispatch = useDispatch();
  const fields = useScopedSelector(({ values }: MfaValidationPageState) => values?.[informationType], 'sms');
  const { [informationType]: information } = useScopedSelector(({ values }: MfaValidationPageState) => values.mfa.informations);

  useEffect(() => {
    dispatch(commitWorkflowStepByName('workflows.mfa', informationType, {
      codeRequestedFor: null,
    }));
  }, [dispatch, informationType]);

  const handleSubmit = useCallback(() => {
    dispatch(moveWorkflowForwardByName('workflows.mfa', {
      ...fields,
      codeRequestedFor: information,
    }));
  }, [dispatch, fields, information]);

  const transformRequest = useCallback((data: MfaInformations): { target: string } => ({
    target: method === 'email' ? data.email : data.phone,
  }), [method]);

  return (
    <>
      <FormPageTitle data-testid="mfaTitle">{t(`steps.${informationType}.title`)}</FormPageTitle>
      <Block white p-h={0} p-t={0}>
        <InformationsSection
          Icon={DialpadIcon}
          data-testid="mfaInfo"
          informationsLabel={t(`steps.${informationType}.info`, { information: formatInfo(information) })}
        />
        <FormSection>
          <CreateForm
            url={`${getIdentityManagementApiRoot()}/${method}/challenge`}
            name={`forms.${informationType}`}
            valuesDst={`values.${informationType}`}
            dataSrc="values.mfa.informations"
            dataDst={`data.${informationType}`}
            defaultCol={12}
            messageBottomBorder={36}
            transformRequest={transformRequest}
            onSuccess={handleSubmit}
          >
            {children}
            <MfaActionBar>
              <StyledSubmitButton data-testid={`submit-${method}`}>{submitButtonLabel}</StyledSubmitButton>
            </MfaActionBar>
            <Spacing m-t={36}>
              <InternalLink href={Constants.MfaInformationsStepSegment}>{t(`steps.${informationType}.cancelButtonText`)}</InternalLink>
            </Spacing>
          </CreateForm>
        </FormSection>
      </Block>
    </>
  );
};

export default MfaCodeGeneration;
